import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import { AxiosError } from "axios";

interface AdminSupportRequestDetails {
  page: number;
  record: number;
  search: string;
  sortOrder: string;
  sortColumn: string;
  target?: "table" | "modal";
}

type AdminSupportRecord = Record<
  string,
  string | number | boolean | string[] | null | undefined
>;

interface AdminSupportData {
  Page: number;
  Record: number;
  TotalRecord: number;
  TotalPages: number;
  Result: AdminSupportRecord[];
}

interface AdminSupportState {
  isLoading: boolean;
  data: AdminSupportData | null;
  error: string | null;
  modalIsLoading: boolean;
  modalData: AdminSupportData | null;
  modalError: string | null;
  isAddingUser: boolean;
  addUserError: string | null;
  isEditingUser: boolean;
  editUserError: string | null;
  isDeletingUser: boolean;
  deleteUserError: string | null;
}

interface AdminSupportApiResponse {
  Status?: string;
  Message?: string;
  message?: string;
  Data?: AdminSupportData;
}

interface ResponseError {
  message: string;
}

const initialData: AdminSupportData = {
  Page: 1,
  Record: 0,
  TotalRecord: 0,
  TotalPages: 0,
  Result: [],
};

export type SystemRoles = "system_admin" | "system_support";

export type AddUserParams = {
  Action: "Add";
  Name: string;
  Email: string;
  IsActive: boolean;
  Role: SystemRoles;
};

export type EditUserParams = {
  Action: "Edit";
  Name: string;
  Email: string;
  IsActive: boolean;
  Role: SystemRoles;
};

export type UpdateUserStatusParams = {
  Action: "updateStatus";
  Email: string;
  IsActive: boolean;
  Role: SystemRoles;
};

export type DeleteUserParams = {
  Action: "Delete";
  UserId: number;
};

export interface ApiResponse<T> {
  Code: number;
  Message: string;
  Status: string;
  Data: T;
  TimeStamp: string;
}

export interface AddUserData {
  UserId: number;
  RoleDetails: Record<string, unknown>;
}

export interface DeleteUserData {
  UserId: number;
}

export type AddUserResponse = ApiResponse<AddUserData>;
export type DeleteUserResponse = ApiResponse<DeleteUserData>;

const initialState: AdminSupportState = {
  isLoading: false,
  data: null,
  error: null,
  modalIsLoading: false,
  modalData: null,
  modalError: null,
  isAddingUser: false,
  addUserError: null,
  isEditingUser: false,
  editUserError: null,
  isDeletingUser: false,
  deleteUserError: null,
};

const AddUserError =
  "An error occurred while adding the user. Please try again.";
const DeleteUserError =
  "An error occurred while deleting the user. Please try again.";
const EditUserError =
  "An error occurred while editing the user. Please try again.";

export const fetchAdminSupportListData = createAsyncThunk<
  AdminSupportApiResponse,
  AdminSupportRequestDetails,
  { rejectValue: ResponseError }
>(
  "AdminSupportSlice/fetchAdminSupportListData",
  async (requestDetails, thunkApi) => {
    const sortOrder =
      requestDetails.sortOrder?.toUpperCase() === "DESC" ? "DESC" : "ASC";
    const sortColumn = requestDetails.sortColumn || "Name";
    const roles = ["system_admin", "system_support"];
    const encodedRoles = roles.map((role) => btoa(role));

    try {
      const response = await httpClient.post("/systemuser/list", {
        Action: "List",
        Roles: encodedRoles,
        Page: requestDetails.page,
        Record: requestDetails.record,
        SortOrder: sortOrder,
        SortColumn: sortColumn,
        Search: requestDetails.search,
      });

      if (response.status !== 200 || response.data?.Status === "error") {
        return thunkApi.rejectWithValue({
          message:
            response.data?.Message ||
            response.data?.message ||
            "Failed to get admin support list",
        });
      }
      return response.data;
    } catch (error) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get admin support list",
      });
    }
  },
);

const handleError = (error: unknown, genericMessage: string) => {
  if (!(error instanceof AxiosError)) {
    return genericMessage;
  }

  const data = error.response?.data;
  if (data?.message) return data.message;
  if (data?.Data && data?.Data.length) return data.Data.join(", ");
  if (data?.Message) return data.Message;
  return genericMessage;
};

export const addAdminSupportUser = createAsyncThunk<
  AddUserResponse,
  AddUserParams,
  { rejectValue: ResponseError }
>("AdminSupportSlice/addAdminSupportUser", async (requestDetails, thunkApi) => {
  try {
    const response = await httpClient.post<AddUserResponse>(
      "/systemuser/add",
      requestDetails,
    );
    if (response.status !== 201 || response.data?.Status === "error") {
      return thunkApi.rejectWithValue({
        message: response.data?.Message || AddUserError,
      });
    }
    return response.data;
  } catch (error) {
    const message = handleError(error, AddUserError);
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({
      message,
    });
  }
});

export const editAdminSupportUser = createAsyncThunk<
  AddUserResponse,
  EditUserParams,
  { rejectValue: ResponseError }
>(
  "AdminSupportSlice/editAdminSupportUser",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post<AddUserResponse>(
        "/systemuser/edit",
        requestDetails,
      );
      if (
        (response.status !== 200 && response.status !== 201) ||
        response.data?.Status === "error"
      ) {
        return thunkApi.rejectWithValue({
          message: response.data?.Message || EditUserError,
        });
      }
      return response.data;
    } catch (error) {
      const message = handleError(error, EditUserError);
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message,
      });
    }
  },
);

export const updateAdminSupportUserStatus = createAsyncThunk<
  any,
  UpdateUserStatusParams,
  { rejectValue: ResponseError }
>(
  "AdminSupportSlice/updateAdminSupportUserStatus",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post<any>(
        "/systemuser/status",
        requestDetails,
      );
      if (response.status !== 200 || response.data?.Status === "error") {
        return thunkApi.rejectWithValue({
          message:
            response.data?.Message ||
            "An error occurred while updating status. Please try again.",
        });
      }
      return response.data;
    } catch (error) {
      const message = handleError(
        error,
        "An error occurred while updating status. Please try again.",
      );
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message,
      });
    }
  },
);

export const deleteAdminSupportUser = createAsyncThunk<
  DeleteUserResponse,
  DeleteUserParams,
  { rejectValue: ResponseError }
>(
  "AdminSupportSlice/deleteAdminSupportUser",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post<DeleteUserResponse>(
        "/systemuser/delete",
        requestDetails,
      );
      if (response.status !== 200 || response.data?.Status === "error") {
        return thunkApi.rejectWithValue({
          message: response.data?.Message || DeleteUserError,
        });
      }
      return response.data;
    } catch (error) {
      console.log("🚀 ~ error:", error);
      const message = handleError(error, DeleteUserError);
      return thunkApi.rejectWithValue({
        message,
      });
    }
  },
);

const AdminSupportSlice = createSlice({
  name: "AdminSupportSlice",
  initialState,
  reducers: {
    resetAdminSupportListState: (state) => {
      state.isLoading = false;
      state.data = initialData;
      state.error = null;
    },
    resetAddUserError: (state) => {
      state.addUserError = null;
    },
    resetDeleteUserError: (state) => {
      state.deleteUserError = null;
    },
    resetEditUserError: (state) => {
      state.editUserError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminSupportListData.pending, (state, action) => {
        if (action.meta.arg.target === "modal") {
          state.modalIsLoading = true;
          state.modalError = null;
          state.modalData = null;
          return;
        }

        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchAdminSupportListData.fulfilled,
        (
          state,
          action: PayloadAction<
            AdminSupportApiResponse,
            string,
            { arg: AdminSupportRequestDetails }
          >,
        ) => {
          const isModalRequest = action.meta.arg.target === "modal";

          if (action.payload?.Status === "success") {
            if (isModalRequest) {
              state.modalIsLoading = false;
              state.modalData = action.payload.Data || initialData;
              state.modalError = null;
            } else {
              state.isLoading = false;
              state.data = action.payload.Data || initialData;
              state.error = null;
            }
          } else {
            const errorMessage =
              action.payload?.Message || "Failed to get admin support list";

            if (isModalRequest) {
              state.modalIsLoading = false;
              state.modalData = null;
              state.modalError = errorMessage;
            } else {
              state.isLoading = false;
              state.data = null;
              state.error = errorMessage;
            }
          }
        },
      )
      .addCase(fetchAdminSupportListData.rejected, (state, action) => {
        const errorMessage =
          action.payload?.message || "Failed to get admin support list";

        if (action.meta.arg.target === "modal") {
          state.modalIsLoading = false;
          state.modalData = null;
          state.modalError = errorMessage;
          return;
        }

        state.isLoading = false;
        state.data = null;
        state.error = errorMessage;
      })

      // Add User
      .addCase(addAdminSupportUser.pending, (state) => {
        state.isAddingUser = true;
        state.addUserError = null;
      })
      .addCase(addAdminSupportUser.fulfilled, (state) => {
        state.isAddingUser = false;
        state.addUserError = null;
      })
      .addCase(addAdminSupportUser.rejected, (state, action) => {
        state.isAddingUser = false;
        state.addUserError = action.payload?.message || AddUserError;
      })
      // edit User
      .addCase(editAdminSupportUser.pending, (state) => {
        state.isEditingUser = true;
        state.editUserError = null;
      })
      .addCase(editAdminSupportUser.fulfilled, (state) => {
        state.isEditingUser = false;
        state.editUserError = null;
      })
      .addCase(editAdminSupportUser.rejected, (state, action) => {
        state.isEditingUser = false;
        state.editUserError = action.payload?.message || EditUserError;
      })
      // Update User Status
      .addCase(updateAdminSupportUserStatus.fulfilled, (state, action) => {
        const email = action.meta.arg.Email;
        const newIsActive = action.meta.arg.IsActive;
        if (state.data?.Result) {
          state.data.Result = state.data.Result.map((row) =>
            row.Email === email ? { ...row, IsActive: newIsActive } : row,
          );
        }
      })

      // Delete User
      .addCase(deleteAdminSupportUser.pending, (state) => {
        state.isDeletingUser = true;
        state.deleteUserError = null;
      })
      .addCase(deleteAdminSupportUser.fulfilled, (state) => {
        state.isDeletingUser = false;
        state.deleteUserError = null;
      })
      .addCase(deleteAdminSupportUser.rejected, (state, action) => {
        state.isDeletingUser = false;
        state.deleteUserError = action.payload?.message || DeleteUserError;
      });
  },
});

export const {
  resetAdminSupportListState,
  resetAddUserError,
  resetDeleteUserError,
  resetEditUserError,
} = AdminSupportSlice.actions;

export default AdminSupportSlice.reducer;
